import { batch } from "solid-js";
import { Bot } from "../classes/Bot.js";
import { PublicBot } from "../classes/PublicBot.js";
import { hydrate } from "../hydration/index.js";
import { ClassCollection } from "./Collection.js";
/**
 * Collection of Bots
 */
export class BotCollection extends ClassCollection {
    /**
     * Fetch bot by ID
     * @param id Id
     * @returns Bot
     */
    async fetch(id) {
        const bot = this.get(id);
        if (bot)
            return bot;
        const data = await this.client.api.get(`/bots/${id}`);
        this.client.users.getOrCreate(data.user._id, data.user);
        return this.getOrCreate(data.bot._id, data.bot);
    }
    /**
     * Fetch owned bots
     * @returns List of bots
     */
    async fetchOwned() {
        const data = (await this.client.api.get("/bots/@me"));
        return batch(() => {
            data.users.forEach((user) => this.client.users.getOrCreate(user._id, user));
            return data.bots.map((bot) => this.getOrCreate(bot._id, bot));
        });
    }
    /**
     * Fetch public bot by ID
     * @param id Id
     * @returns Public Bot
     */
    async fetchPublic(id) {
        const data = await this.client.api.get(`/bots/${id}/invite`);
        return new PublicBot(this.client, data);
    }
    /**
     * Get or create
     * @param id Id
     * @param data Data
     * @returns Bot
     */
    getOrCreate(id, data) {
        if (this.has(id)) {
            // Merge fresh API data into the existing instance's underlying object.
            // Bot hydration is scalar-only (id, ownerId, token, flags, urls, etc),
            // no reactive containers to carve out — straightforward replacement.
            this.updateUnderlyingObject(id, hydrate("bot", data, this.client, false));
            return this.get(id);
        }
        else {
            const instance = new Bot(this, id);
            this.create(id, "bot", instance, this.client, data);
            return instance;
        }
    }
    /**
     * Create a bot
     * @param name Bot name
     * @returns The newly-created bot
     */
    async createBot(name) {
        const { user, ...bot } = await this.client.api.post(`/bots/create`, {
            name,
        });
        this.client.users.getOrCreate(user._id, user);
        return this.getOrCreate(bot._id, bot);
    }
}
